
/// <reference types="vite/client" />
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

// Create the main API client
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Helper function to get auth store (to avoid circular dependency)
const getAuthStore = () => {
  // Using dynamic import to avoid circular dependency
  return import('../store/authStore').then(module => module.useAuthStore);
};

// Add token to request
const addAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Queue failed requests while refreshing token
const addSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Process queued requests after token refresh
const processQueue = (error: Error | null, token: string | null = null) => {
  refreshSubscribers.forEach(callback => {
    if (error) {
      // Token refresh failed - these requests will need to handle auth failure
      console.log('Token refresh failed, request will be retried but may fail');
    } else if (token) {
      callback(token);
    }
  });
  
  refreshSubscribers = [];
};

// Handle token refresh
const handleTokenRefresh = async (): Promise<string | null> => {
  try {
    const authStore = await getAuthStore();
    const store = authStore.getState();
    
    if (!store.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Call the refresh method from auth store
    await store.refreshAccessToken();
    
    const newToken = localStorage.getItem('accessToken');
    if (!newToken) {
      throw new Error('Token refresh did not provide new access token');
    }

    return newToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    
    // Show user notification
    toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // Auto logout user
    const authStore = await getAuthStore();
    const store = authStore.getState();
    await store.logout();

    // Redirect to login page
    window.location.href = '/login';

    throw error;
  }
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return addAuthToken(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    const responseData = error.response?.data as { code?: string; message?: string } | undefined;
    const errorCode = responseData?.code;

    if (errorCode === 'ACCOUNT_LOCKED' || (error.response?.status === 403 && errorCode === 'ACCOUNT_LOCKED')) {
      toast.error(
        responseData?.message ||
          'Tài khoản đã bị khóa do nghi ngờ chia sẻ. Vui lòng liên hệ quản trị viên.',
        { autoClose: 8000 }
      );
      try {
        const authStore = await getAuthStore();
        await authStore.getState().logout();
      } catch {
        /* ignore */
      }
      window.location.href = '/login?locked=1';
      return Promise.reject(error);
    }

    if (errorCode === 'DEVICE_LIMIT') {
      toast.error(
        responseData?.message ||
          'Đã đạt giới hạn 4 thiết bị. Vui lòng liên hệ admin.',
        { autoClose: 8000 }
      );
      return Promise.reject(error);
    }
    
    // Check if this is a 401 error and we haven't already tried to refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.headers['X-Retry-After-Refresh'] &&
      errorCode !== 'ACCOUNT_LOCKED'
    ) {
      
      if (isRefreshing) {
        // If we're already refreshing, queue this request
      return new Promise((resolve) => {
          addSubscriber((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              originalRequest.headers['X-Retry-After-Refresh'] = 'true';
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      // Start the refresh process
      isRefreshing = true;

      try {
        const newToken = await handleTokenRefresh();
        
        if (newToken) {
          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest.headers['X-Retry-After-Refresh'] = 'true';
          }
          
          // Process queued requests
          processQueue(null, newToken);
          
          // Retry the original request
          return apiClient(originalRequest);
        }
        
      } catch (refreshError) {
        // Token refresh failed
        processQueue(refreshError as Error, null);
        return Promise.reject(refreshError);
        
      } finally {
        isRefreshing = false;
      }
    }

    // If it's not a 401 or refresh failed, return the original error
    return Promise.reject(error);
  }
);

export default apiClient;

// Export a configured axios instance for general API calls
export { apiClient };

// Utility function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  return !!(accessToken && refreshToken);
};

// Utility function to get current auth token
export const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken');
};